import { Hymn } from "@/services/hymnsApi";
import { getHymnSlug, scrollToTop } from "@/utils";
import {
  AlertTriangle,
  ChevronRight,
  Flame,
  Music,
  RefreshCw,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

interface PopularHymnsSectionProps {
  hymns: Hymn[];
  loading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

export const PopularHymnsSection: React.FC<PopularHymnsSectionProps> = ({
  hymns,
  loading = false,
  hasError = false,
  onRetry,
}) => {
  const navigate = useNavigate();

  const handleOpenSong = (hymn: Hymn) => {
    scrollToTop();
    const slug = getHymnSlug(hymn.number, hymn.title);
    navigate(
      `/chants-desperance/${encodeURIComponent(hymn.section_code)}/${slug}`,
    );
  };

  const getExcerpt = (hymn: Hymn): string => {
    if (hymn.verses && Array.isArray(hymn.verses) && hymn.verses.length > 0) {
      const first = hymn.verses[0];
      if (typeof first === "string") return first.substring(0, 95) + "...";
      if (first.content) return first.content.substring(0, 95) + "...";
      if (first.text) return first.text.substring(0, 95) + "...";
      if (first.lines && Array.isArray(first.lines)) {
        return first.lines.slice(0, 2).join(" / ") + "...";
      }
    }
    if (hymn.full_text) {
      return hymn.full_text.substring(0, 95) + "...";
    }
    return "Louange et adoration pour fortifier notre foi en Jésus-Christ.";
  };

  return (
    <section className="relative w-full py-12">
      <div className="container mmax-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre de section en français */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              <Flame className="w-8 h-8 text-amber-400 fill-amber-400 animate-pulse inline" />{" "}
              Cette semaine
            </h2>
          </div>
        </div>

        {/* Skeleton Loader animé */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl bg-slate-900/80 border border-slate-800 animate-pulse space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <div className="h-6 w-14 bg-slate-800 rounded-lg" />
                    <div className="h-6 w-10 bg-slate-800 rounded-lg" />
                  </div>
                  <div className="h-5 w-12 bg-slate-800 rounded-full" />
                </div>
                <div className="h-5 bg-slate-800 rounded w-4/5" />
                <div className="h-4 bg-slate-800/60 rounded w-full" />
                <div className="h-4 bg-slate-800/60 rounded w-3/4" />
                <div className="pt-3 border-t border-slate-800/80 flex justify-between">
                  <div className="h-3 bg-slate-800 rounded w-1/3" />
                  <div className="h-3 bg-slate-800 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* État d'erreur gracieux */}
        {!loading && hasError && (
          <div className="text-center py-12 px-6 bg-slate-900/80 rounded-3xl border border-amber-500/30 backdrop-blur-md shadow-2xl max-w-xl mx-auto my-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4 text-amber-400">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              Impossible de charger les cantiques populaires
            </h3>
            <p className="text-slate-300 text-sm mb-6 max-w-md mx-auto leading-relaxed">
              La liste des cantiques recommandés n'a pas pu être récupérée pour
              le moment.
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20 transition-all hover:scale-105"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Réessayer</span>
              </button>
            )}
          </div>
        )}

        {/* Grille des cantiques de l'assemblée */}
        {!loading && !hasError && hymns.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {hymns.map((hymn, idx) => {
              const hasAudio = Boolean(hymn.media_url);

              return (
                <div
                  key={hymn.id || `${hymn.section_code}-${hymn.number}-${idx}`}
                  onClick={() => handleOpenSong(hymn)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleOpenSong(hymn);
                    }
                  }}
                  className="group relative flex flex-col justify-between p-5 rounded-2xl bg-slate-900/90 hover:bg-slate-800/95 border border-slate-700/60 hover:border-amber-400/60 shadow-xl hover:shadow-2xl hover:shadow-amber-500/10 backdrop-blur-md transition-all duration-300 cursor-pointer overflow-hidden transform hover:-translate-y-1 text-left"
                >
                  {/* Liseré supérieur doré */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-rx from-amber-500 via-sky-500 to-amber-500 opacity-70 group-hover:opacity-100 transition-opacity" />

                  <div>
                    {/* En-tête de la carte */}
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-lg bg-slate-800 text-amber-300 font-bold text-xl border border-slate-700">
                          N° {hymn.number}
                        </span>
                      </div>

                      {false && hasAudio && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-950/80 border border-emerald-500/40 text-emerald-300">
                          <Music className="w-3 h-3" />
                        </span>
                      )}
                    </div>

                    {/* Titre du cantique */}
                    <h2 className="font-bold text-white text-lg group-hover:text-amber-300 transition-colors line-clamp-2 leading-snug mb-2">
                      {hymn.title}
                    </h2>

                    {/* Extrait */}
                    {false && (
                      <p className="text-slate-300 text-xs sm:text-sm line-clamp-2 leading-relaxed italic">
                        "{getExcerpt(hymn)}"
                      </p>
                    )}
                  </div>

                  {/* Pied de carte */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-800/80">
                    <span className="text-xs font-semibold text-slate-400 group-hover:text-sky-300 transition-colors truncate max-w-[60%]">
                      {hymn.section}
                    </span>

                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-400 group-hover:translate-x-1 transition-transform">
                      Voir
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
